/**
 * Search gate — fast client-side check that the user has a registered vehicle
 * before allowing a plate search. The new SMS-only login flow lets the user
 * in WITHOUT a vehicle; we then gate the search behind this prompt to push
 * them through the vehicle-registration sub-flow.
 *
 * Two public entry points:
 *   - `hasRegisteredVehicle()`     — boolean, instant (LocalState only)
 *   - `requireVehicleOrPrompt()`   — boolean; if false, shows an explanation
 *                                    alert with a CTA to open the register
 *                                    modal. The caller should `return` on false.
 */

import { LocalState } from '../../core/localState.js';
import * as alert from '../../core/alert.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('searchGate');

/**
 * Returns `true` when the user is allowed to perform a plate search.
 * Fast — does NOT hit the network. Two acceptance paths:
 *
 *   1. User has a plate registered (the normal case). Matches what
 *      `validateSession` accepts as a "full account" (uid + phone + plate).
 *   2. Admin has flipped `users/{uid}/searchBypass = true` for this user —
 *      mirrored to `LocalState.searchBypass` via the live listener wired in
 *      auth.service. Used to grant access to journalists, support staff,
 *      partners, etc. without forcing them to register a stub vehicle.
 *
 * @returns {boolean}
 */
export function hasRegisteredVehicle() {
  if (LocalState.searchBypass) return true;
  const plate = String(LocalState.plate || '').replace(/\D/g, '');
  return plate.length >= 7;
}

/**
 * If the user has a vehicle, return true. Otherwise show an explanatory
 * alert with a "רשום רכב עכשיו" CTA that opens the vehicle-registration
 * modal, and return false so the caller can short-circuit.
 *
 * Uses `alert.showAlert` with `okLabel` — the built-in modal renders OK as
 * the primary button. The CTA copy doubles as the action: when the user
 * taps it we open the register modal directly.
 *
 * @returns {Promise<boolean>}
 */
export async function requireVehicleOrPrompt() {
  if (hasRegisteredVehicle()) return true;

  log.info('search blocked — no registered vehicle');
  const choice = await alert.showConfirm({
    title: 'נדרש רכב רשום לחיפוש',
    message:
      'כדי לחבר אותך לבעל הרכב בלי לחשוף את מספר הטלפון שלך, לוחית הרכב שלך ' +
      'משמשת כ - "מספר טלפון" אנונימי. עלייך לרשום קודם את לוחית הרכב שלך ' +
      'לחשבון - זהו תהליך מהיר וחד־פעמי.',
    confirm: '🚗 רשום רכב עכשיו',
    cancel: 'אולי אחר כך',
  });
  if (!choice) return false;

  // Open the unified vehicle modal — `replaceVehicle.view.js::open()` is now
  // context-aware (detects register vs replace from LocalState.plate) so a
  // single modal serves both flows. Routing through the same window global
  // the personal area uses (`openReplaceModal`) means both entry points share
  // identical UI + post-success refresh path.
  const w = /** @type {any} */ (window);
  if (typeof w.openReplaceModal === 'function') {
    w.openReplaceModal();
  } else {
    log.warn('openReplaceModal shim missing');
  }
  // We can't await the modal's close from here — return false so the caller
  // short-circuits the search. The user will retry after registration completes.
  return false;
}
